import React from 'react';
import clsx from 'clsx';
import * as R from 'ramda';
import {Link, useLocation, useNavigate} from '@remix-run/react';
import {useMatches} from '@remix-run/react';
import {Image} from '@shopify/hydrogen';

//domains
import {CurrencySettingsGlobal, formatCurrency} from 'domains/currency';
import type {PredictiveSearchPayload} from '~/routes/($lang)/api/predictive-search';

export default function HeaderSearch() {
  const [results, setResults] = React.useState<null | PredictiveSearchPayload>(
    null,
  );

  const [query, setQuery] = React.useState<string>('');
  const [root] = useMatches();
  const currency = root.data.currency as CurrencySettingsGlobal;
  const translation = root?.data?.translation;

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const {pathname} = useLocation();

  // Native predictive search via our own API route (Boost suggest replaced);
  // debounced so we don't fire a request per keystroke. The locale prefix
  // keeps the "View all N" count in the same language context as the /search
  // page the link leads to.
  const localePrefix =
    (root?.data?.selectedLocale as {pathPrefix?: string} | undefined)
      ?.pathPrefix ?? '';
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  const search = React.useCallback(
    (searchQuery: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch(
            `${localePrefix}/api/predictive-search?q=${encodeURIComponent(
              searchQuery,
            )}`,
          );
          const data: PredictiveSearchPayload = await res.json();
          setResults(data);
        } catch {
          // network hiccup — keep the previous suggestions
        }
      }, 250);
    },
    [localePrefix],
  );

  return (
    <div
      className={clsx(
        'relative w-full overflow-y-scroll md:h-auto md:overflow-visible',
        isOpen && query ? 'h-screen' : 'h-auto',
      )}
      onFocus={() => setIsOpen(true)}
    >
      <button
        className={clsx([
          'absolute top-0 left-0 h-full w-screen block bg-black bg-opacity-20 md:fixed md:left-1/2 md:h-screen md:-translate-x-1/2 md:bg-transparent',
          isOpen ? 'md:visible' : 'md:invisible',
          isOpen && query ? 'visible' : 'invisible',
        ])}
        onClick={() => setIsOpen(false)}
      ></button>
      <div
        // On mobile this bar is position:fixed (out of the header's flow), so
        // Layout.tsx measures it via this hook and adds its height to <main>'s
        // top padding — otherwise the fixed bar overlaps the hero banner top.
        data-mobile-search-bar
        className={clsx(
          'fixed z-[100] flex w-full items-center space-x-2 bg-white px-5 py-2 md:relative md:py-0 md:px-0',
          !isOpen && 'lg:shadow-none border-b lg:border-none',
          !pathname.includes('collections') && 'shadow-md',
        )}
      >
        <div className="relative z-10 flex w-full items-center border-[1px] border-solid border-black md:w-full">
          <SearchInput
            setQuery={setQuery}
            query={query}
            search={search}
            setIsOpen={setIsOpen}
          />
          <Link
            to={`${localePrefix}/search?q=${encodeURIComponent(query)}`}
            className="absolute right-0 top-0 hidden h-full w-[60px] items-center justify-center bg-hk-orange md:flex"
          >
            <SearchIcon />
          </Link>
        </div>
        {isOpen && query && (
          <button className="md:hidden" onClick={() => setIsOpen(false)}>
            <CrossIcon />
          </button>
        )}
      </div>
      <div
        className={clsx([
          'absolute top-0 max-h-[75svh] translate-y-[50px] z-10 h-auto w-full bg-white pt-4 text-black md:top-full md:left-1/2 md:max-h-[400px] md:w-[200%] md:-translate-x-1/2 md:translate-y-4 overflow-y-scroll md:border md:shadow-md lg:w-auto lg:min-w-[500px]',
          isOpen && query ? 'visible opacity-100' : 'invisible opacity-0',
        ])}
      >
        {results?.collections && !R.isEmpty(results.collections) ? (
          <div className="mb-4">
            <header className="mb-2 ml-2 border-b border-solid pb-1">
              <h4 className="uppercaase text-sm text-left">
                {translation?.search?.categories_label ?? 'Categories'}
              </h4>
            </header>
            <ul className="">
              {results.collections.map((collection) => {
                return (
                  <li
                    key={collection.handle}
                    className="px-4 py-2 hover:bg-slate-300 hover:bg-opacity-20 text-left"
                  >
                    <Link to={`/collections/${collection.handle}`}>
                      <button onClick={() => setIsOpen(false)}>
                        {collection.title}
                      </button>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {results?.products && !R.isEmpty(results.products) ? (
          <div className="">
            <header className="mb-2 ml-2 border-b border-solid pb-1">
              <h4 className="uppercaase text-sm text-left">
                {translation?.search?.product_label ?? 'Products'}
              </h4>
            </header>
            <ul className="">
              {results.products.map((product) => {
                return (
                  <li
                    key={product.id}
                    className="px-4 py-2 hover:bg-slate-300 hover:bg-opacity-20"
                  >
                    <Link to={`/products/${product.handle}`}>
                      <button onClick={() => setIsOpen(false)}>
                        <div className="flex space-x-4">
                          <div className="">
                            {product.image && (
                              <Image
                                data={{
                                  url: product.image,
                                  altText: product.title,
                                }}
                                width={100}
                                height={100}
                              />
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <h5 className="line-clamp-1text-sm font-normal">
                              {product.title}
                            </h5>
                            {!product.available && (
                              <span className="text-xs">{`[Out Of Stock]`}</span>
                            )}
                            <p className="line-clamp-3 mt-2 text-xs font-light">
                              {product.description}
                            </p>
                            <div className="mt-2 text-sm">
                              {(() => {
                                const shown = formatCurrency({
                                  price: {
                                    amount: product.price,
                                    currencyCode: 'HKD',
                                  },
                                  currency,
                                });
                                if (!shown) return null;
                                // The symbol is looked up from the currency
                                // the amount was actually converted to. It
                                // used to come from `baseCurrency`, so a
                                // shopper browsing in USD saw a converted
                                // number wearing the base currency's symbol.
                                const symbol =
                                  currency.all_currencies.find((c) =>
                                    c.currency.endsWith(
                                      `-${shown.currencyCode}`,
                                    ),
                                  )?.details?.currency?.symbol ??
                                  `${shown.currencyCode} `;
                                return `${symbol}${shown.amount.toFixed(2)}`;
                              })()}
                            </div>
                          </div>
                        </div>
                      </button>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="p-2 text-sm font-normal">
            {translation?.searach?.no_product_message ?? 'No product found'}
          </div>
        )}
        {results?.totalProducts !== 0 && (
          <div className="">
            <Link
              to={`${localePrefix}/search?q=${encodeURIComponent(query)}`}
              className="py-2 block w-full text-center bg-hk-orange"
            >
              <button onClick={() => setIsOpen(false)}>
                View all {results?.totalProducts} products
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function SearchInput({
  setQuery,
  search,
  query,
  setIsOpen,
}: {
  setQuery: (query: string) => void;
  search: (query: string) => void;
  query: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [root] = useMatches();
  const translation = root?.data?.translation;
  const localePrefix =
    (root?.data?.selectedLocale as {pathPrefix?: string} | undefined)
      ?.pathPrefix ?? '';
  const navigate = useNavigate();
  return (
    <input
      type="text"
      className="text-[16px] md:text-sm w-full flex-1 px-2 py-1 md:py-2 md:px-4 outline-none placeholder:font-normal text-black placeholder:text-black md:w-auto border-none focus:outline-none"
      placeholder={translation?.search?.search_label ?? 'Search'}
      onChange={async (e) => {
        setQuery(e.target.value);
        if (e.target.value) {
          await search(e.target.value);
        }
      }}
      onKeyDown={(event) => {
        if (query && event.key === 'Enter') {
          navigate(`${localePrefix}/search?q=${encodeURIComponent(query)}`);
          setIsOpen(false);
        }
      }}
      value={query}
    />
  );
}

function CrossIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 9.35849L1.85849 15L0 13.1415L5.64151 7.5L0 1.85849L1.85849 0L7.5 5.64151L13.1415 0L15 1.85849L9.35849 7.5L15 13.1415L13.1415 15L7.5 9.35849Z"
        fill="black"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="20"
      height="20"
      xmlns="http://www.w3.org/2000/svg"
      fillRule="evenodd"
      clipRule="evenodd"
    >
      <path d="M15.853 16.56c-1.683 1.517-3.911 2.44-6.353 2.44-5.243 0-9.5-4.257-9.5-9.5s4.257-9.5 9.5-9.5 9.5 4.257 9.5 9.5c0 2.442-.923 4.67-2.44 6.353l7.44 7.44-.707.707-7.44-7.44zm-6.353-15.56c4.691 0 8.5 3.809 8.5 8.5s-3.809 8.5-8.5 8.5-8.5-3.809-8.5-8.5 3.809-8.5 8.5-8.5z" />
    </svg>
  );
}
